import { useState } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle, X, Sparkles } from "lucide-react";
import { getPageHelp } from "@/help/pageHelp";

interface PageHelpProps {
  /** Optional override route — falls back to current pathname. */
  pathname?: string;
  /** Optional callback to ask the Helper widget a question. */
  onAskHelper?: (initialMessage: string) => void;
}

// Markdown rendering kept dead-simple: bold, inline code, line breaks.
// We don't pull a full MD lib for ~10 short help blurbs.
function renderInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let i = 0;
  let buf = "";
  const flush = () => {
    if (buf) out.push(buf);
    buf = "";
  };
  while (i < text.length) {
    if (text.slice(i, i + 2) === "**") {
      const end = text.indexOf("**", i + 2);
      if (end !== -1) {
        flush();
        out.push(<strong key={i} className="text-gray-100">{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    if (text[i] === "`") {
      const end = text.indexOf("`", i + 1);
      if (end !== -1) {
        flush();
        out.push(
          <code key={i} className="rounded bg-gray-800 px-1 font-mono text-xs text-amber-300">
            {text.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }
    buf += text[i];
    i += 1;
  }
  flush();
  return out;
}

function renderMarkdown(body: string): React.ReactNode {
  return body.split("\n").map((line, idx) => {
    if (!line.trim()) return <div key={idx} className="h-2" />;
    return (
      <p key={idx} className="text-sm leading-relaxed text-gray-300">
        {renderInline(line)}
      </p>
    );
  });
}

export default function PageHelp({ pathname, onAskHelper }: PageHelpProps) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const help = getPageHelp(pathname ?? location.pathname);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Aide contextuelle"
        className="inline-flex items-center gap-1.5 rounded border border-gray-700 bg-gray-900 px-2 py-1 text-xs text-gray-300 hover:border-blue-600 hover:bg-gray-800"
      >
        <HelpCircle className="h-3.5 w-3.5" />
        Aide
      </button>
      {open && (
        <div
          className="fixed inset-0 z-40 flex justify-end bg-black/40"
          onClick={() => setOpen(false)}
        >
          <aside
            className="h-full w-full max-w-md overflow-y-auto border-l border-gray-800 bg-gray-950 p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-100">
                <HelpCircle className="h-5 w-5 text-blue-400" />
                {help.title}
              </h2>
              <button
                onClick={() => setOpen(false)}
                className="rounded p-1 text-gray-500 hover:bg-gray-800 hover:text-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-1">{renderMarkdown(help.body)}</div>
            {onAskHelper && (
              <div className="mt-6 border-t border-gray-800 pt-4">
                <button
                  onClick={() => {
                    onAskHelper(`Sur la page ${pathname ?? location.pathname} : `);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500"
                >
                  <Sparkles className="h-4 w-4" />
                  Poser une question a l'assistant
                </button>
              </div>
            )}
          </aside>
        </div>
      )}
    </>
  );
}
